// src/pages/therapists/ManageTherapistServicesPage.js
import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore'
import { db } from '../../firebaseConfig'

export default function ManageTherapistServicesPage() {
  const navigate = useNavigate()
  const location = useLocation()

  const [therapists, setTherapists] = useState([])
  const [selectedId, setSelectedId] = useState('')
  const [serviceCatalog, setServiceCatalog] = useState([])
  const [serviceSelection, setServiceSelection] = useState({})
  const [loading, setLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false) // Tambahan state ini

  // Ambil ID terapis dari URL
  const queryParams = new URLSearchParams(location.search)
  const therapistIdFromUrl = queryParams.get('id') || ''

  // Load Catalog
  const loadCatalog = async () => {
    const snap = await getDocs(collection(db, 'therapists_services'))
    const items = snap.docs.map((d) => {
      const data = d.data()
      const variantsMap =
        data.variants && typeof data.variants === 'object' ? data.variants : {}
      const variantCodes = Object.keys(variantsMap)

      return {
        id: d.id,
        name: data.name || d.id,
        category: data.category || 'Other',
        active: data.active !== false,
        variants: variantCodes.sort(),
      }
    })
    setServiceCatalog(items)
  }

  // Load Therapists
  const loadTherapists = async () => {
    setLoading(true)
    const snap = await getDocs(collection(db, 'therapists'))
    const data = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      services: Array.isArray(d.data().services) ? d.data().services : [],
    }))

    setTherapists(data)
    setLoading(false)
  }

  useEffect(() => {
    loadCatalog()
    loadTherapists()
  }, [])

  // Auto-select therapist berdasarkan URL
  useEffect(() => {
    // Tambahkan pengecekan serviceCatalog.length > 0
    if (therapistIdFromUrl && therapists.length > 0 && serviceCatalog.length > 0) {
      handleSelectTherapist(therapistIdFromUrl)
      // setSelectedId tidak perlu ditulis lagi karena sudah ada di dalam handleSelectTherapist()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapists, serviceCatalog, therapistIdFromUrl]) // Tambahkan serviceCatalog sebagai dependency

  const groupedCatalog = useMemo(() => {
    const groups = {}
    serviceCatalog
      .filter((s) => s.active)
      .forEach((s) => {
        const cat = s.category || 'Other'
        if (!groups[cat]) groups[cat] = []
        groups[cat].push(s)
      })

    Object.keys(groups).forEach((k) =>
      groups[k].sort((a, b) => a.name.localeCompare(b.name))
    )

    return groups
  }, [serviceCatalog])

  const handleSelectTherapist = (id) => {
    setSelectedId(id)
    const therapist = therapists.find((t) => t.id === id)
    if (!therapist) return

    const sel = {}
    serviceCatalog.forEach((item) => {
      sel[item.id] = {
        name: item.name,
        category: item.category,
        checked: false,
        variants: {},
      }
      item.variants.forEach((v) => {
        sel[item.id].variants[v] = false
      })
    })

    therapist.services.forEach((s) => {
      if (!sel[s.serviceId]) return
      sel[s.serviceId].checked = true

      const arr = Array.isArray(s.variants) ? s.variants : []
      arr.forEach((v) => {
        sel[s.serviceId].variants[v] = true
      })
    })

    setServiceSelection(sel)
  }

  const setServiceChecked = (serviceId, checked) => {
    setServiceSelection((prev) => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        checked,
      },
    }))
  }

  const setVariantChecked = (serviceId, variantCode, checked) => {
    setServiceSelection((prev) => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        variants: {
          ...prev[serviceId].variants,
          [variantCode]: checked,
        },
      },
    }))
  }

  const saveServices = async () => {
    if (!selectedId) return alert('Pilih terapis terlebih dahulu!')

    setIsSaving(true) // Set indikator mulai menyimpan
    try {
      const picked = Object.entries(serviceSelection)
        .map(([serviceId, val]) => {
          const chosenVariants = Object.entries(val.variants)
            .filter(([, on]) => on)
            .map(([code]) => code)

          if (val.checked) {
            return { serviceId, variants: chosenVariants }
          }
          return null
        })
        .filter(Boolean)

      const therapistRef = doc(db, 'therapists', selectedId)
      await updateDoc(therapistRef, { services: picked })

      alert('✅ Layanan terapis diperbarui')
    } catch (error) {
      console.error("Gagal menyimpan data:", error)
      alert('❌ Terjadi kesalahan saat menyimpan data.')
    } finally {
      setIsSaving(false) // Set indikator selesai menyimpan
    }
  }

  return (
    <div className="text-gray-300">
      <h2 className="text-2xl font-bold mb-4">🧾 Kelola Layanan Terapis</h2>

      {/* === KEMBALI KE MENU TERAPIS (FIX ERROR) === */}
      <button
        onClick={() => navigate('/therapists')}
        className="mb-4 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700"
      >
        ◀ Kembali ke Menu Terapis
      </button>

      {/* INDIKATOR LOADING */}
      {loading && <div className="text-blue-400 mb-4 font-medium animate-pulse">Memuat data...</div>}

      {/* PILIH TERAPIS */}
      <div className="bg-gray-900 p-4 rounded-lg border border-gray-800 mb-4">
        <label className="block mb-2 text-gray-300 font-medium">
          Pilih Terapis
        </label>

        <select
          value={selectedId}
          onChange={(e) => handleSelectTherapist(e.target.value)}
          className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
        >
          <option value="">-- Pilih Terapis --</option>
          {therapists.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} {t.id_number ? `(${t.id_number})` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* LIST LAYANAN */}
      {selectedId && (
        <div className="bg-gray-900 p-4 rounded-lg border border-gray-800">
          {Object.keys(groupedCatalog)
            .sort()
            .map((cat) => (
              <div key={cat} className="mb-4 border border-gray-800 rounded-lg">
                <div className="px-3 py-2 bg-gray-800 font-semibold">{cat}</div>

                <div className="p-3 space-y-3">
                  {groupedCatalog[cat].map((svc) => {
                    const selected = serviceSelection[svc.id] || {
                      checked: false,
                      variants: {},
                    }

                    return (
                      <div
                        key={svc.id}
                        className="border border-gray-800 rounded-lg"
                      >
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-850">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selected.checked}
                              onChange={(e) =>
                                setServiceChecked(svc.id, e.target.checked)
                              }
                            />

                            <span className="font-medium">
                              {svc.name}{' '}
                              <span className="text-gray-400 text-xs">
                                ({svc.id})
                              </span>
                            </span>
                          </label>
                        </div>

                        {/* VARIANTS */}
                        <div className="px-3 py-2">
                          {svc.variants.length === 0 ? (
                            <p className="text-gray-500 text-sm italic">
                              Layanan ini tidak memiliki varian.
                            </p>
                          ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {svc.variants.map((code) => (
                                <label
                                  key={code}
                                  className="inline-flex items-center gap-2"
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!selected.variants[code]}
                                    onChange={(e) =>
                                      setVariantChecked(
                                        svc.id,
                                        code,
                                        e.target.checked
                                      )
                                    }
                                    disabled={!selected.checked}
                                  />

                                  <span
                                    className={
                                      !selected.checked ? 'text-gray-500' : ''
                                    }
                                  >
                                    {code}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}

                          <p className="text-xs text-gray-500 mt-2">
                            *Jika semua varian tidak dicentang, layanan disimpan
                            dengan <b>variants: []</b>.
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
        </div>
      )}

      {selectedId && (
        <button
          onClick={saveServices}
          disabled={isSaving} // Nonaktifkan tombol jika sedang memproses
          className={`mt-4 px-4 py-2 rounded-lg text-white font-medium ${
            isSaving 
              ? 'bg-green-800 cursor-not-allowed opacity-70' 
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {isSaving ? 'Menyimpan...' : 'Simpan Perubahan'}
        </button>
      )}
    </div>
  )
}
